/**
 * Reconciliation Dashboard Page
 * 
 * Admin interface for viewing and managing instrument data conflicts
 * 
 * Features:
 * - Real-time reconciliation statistics
 * - Conflict list with filters
 * - Search by exchange/symbol/broker
 * - Navigation to detailed conflict view
 * - Validation failure tracking
 * - Conflict resolution with approve/reject options
 * 
 * Access: Admin-only (requires 'admin' scope)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from "../../../../components";
import Loader from "../../../../components/Loader";
import { useStateContext } from "../../../../contexts/ContextProvider";
import { services } from '../../../../services/api';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { 
  FiAlertCircle, 
  FiRefreshCw, 
  FiSearch,
  FiCheckCircle,
  FiXCircle,
  FiAlertTriangle,
  FiFileText,
  FiEdit,
  FiCheck,
  FiX,
  FiEye
} from 'react-icons/fi';

const ReconciliationDashboard = ({ isEmbedded = false }) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [validationFailures, setValidationFailures] = useState([]);
  
  // Selected conflict for resolution
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [conflictDetail, setConflictDetail] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('conflict_needs_review');
  const [exchangeFilter, setExchangeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 50;

  // Auto-refresh interval
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      showSnackbar('Access denied. Admin privileges required.', 'error');
    }
  }, [user, showSnackbar]);

  // Fetch data on mount and when filters change
  useEffect(() => {
    fetchStats();
    fetchConflicts();
    fetchValidationFailures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, exchangeFilter, currentPage]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchStats();
      fetchConflicts();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, statusFilter, exchangeFilter, currentPage]);

  const fetchStats = async () => {
    try {
      const response = await services.instruments.admin.getReconciliationStats();
      
      // Handle both wrapped {status, data} and direct response formats
      const responseData = response.data?.data || response.data;
      
      if (responseData) {
        setStats({
          total_instruments: responseData.total_instruments || 0,
          single_source: responseData.single_source || 0,
          auto_resolved: responseData.multi_source_match || responseData.auto_resolved_today || 0,
          conflict_needs_review: responseData.total_conflicts || responseData.pending_review || 0,
          validation_failures: responseData.validation_failures || 0,
          manually_resolved_today: responseData.manually_resolved_today || 0,
          last_sync_time: responseData.last_sync_time,
          last_conflict_resolved: responseData.last_conflict_resolved
        });
      }
    } catch (error) {
      console.error('Error fetching reconciliation stats:', error);
      // Show error to user only if it's a significant error
      if (error.response?.status !== 404) {
        showSnackbar(error.message || 'Failed to load reconciliation stats', 'warning');
      }
    }
  };

  const fetchConflicts = async () => {
    try {
      setLoading(true);
      
      // Calculate skip from page number
      const skip = (currentPage - 1) * perPage;
      
      const params = {
        skip: skip,
        limit: perPage,
        ...(statusFilter && { status: statusFilter }),
        ...(exchangeFilter && { exchange: exchangeFilter }),
        ...(searchQuery && { search: searchQuery })
      };

      const response = await services.instruments.admin.getConflicts(params);
      
      // Handle both wrapped {status, data} and direct response formats
      // Backend returns: { conflicts: [...], total, page, limit }
      const responseData = response.data?.data || response.data;
      
      if (responseData) {
        // Backend uses 'conflicts' not 'items'
        const conflictItems = responseData.conflicts || responseData.items || [];
        const total = responseData.total || 0;
        
        setConflicts(conflictItems);
        setTotalRecords(total);
        setTotalPages(Math.ceil(total / perPage) || 1);
      } else {
        setConflicts([]);
        setTotalRecords(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching conflicts:', error);
      showSnackbar(error.message || 'Failed to load conflicts', 'error');
      setConflicts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchValidationFailures = async () => {
    try {
      const response = await services.instruments.admin.getValidationFailures({ limit: 10 });
      
      // Handle both wrapped {status, data} and direct response formats
      const responseData = response.data?.data || response.data;
      
      if (responseData) {
        // Backend uses 'conflicts' not 'items' for validation failures too
        setValidationFailures(responseData.conflicts || responseData.items || []);
      }
    } catch (error) {
      console.error('Error fetching validation failures:', error);
    }
  };

  // Fetch conflict detail for resolution modal
  const fetchConflictDetail = async (instrumentId) => {
    try {
      setResolving(true); // Show loading state while fetching
      console.log('Fetching conflict detail for:', instrumentId);
      
      const response = await services.instruments.admin.getConflictDetail(instrumentId);
      console.log('Conflict detail response:', response);
      
      const responseData = response.data?.data || response.data;
      
      if (responseData) {
        setConflictDetail(responseData);
        setShowResolveModal(true);
      } else {
        console.error('No data in response:', response);
        showSnackbar('No conflict details found', 'warning');
      }
    } catch (error) {
      console.error('Error fetching conflict detail:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to load conflict details';
      showSnackbar(errorMessage, 'error');
    } finally {
      setResolving(false);
    }
  };

  // Resolve a conflict by selecting winning broker
  const handleResolveConflict = async (winningBrokerId, overrideData = null, notes = '') => {
    if (!selectedConflict) return;
    
    try {
      setResolving(true);
      
      const response = await services.instruments.admin.resolveConflict(
        selectedConflict.instrument_id,
        {
          winning_broker_id: winningBrokerId,
          override_data: overrideData,
          resolution_notes: notes
        }
      );
      
      showSnackbar('Conflict resolved successfully!', 'success');
      setShowResolveModal(false);
      setSelectedConflict(null);
      setConflictDetail(null);
      
      // Refresh data
      fetchStats();
      fetchConflicts();
    } catch (error) {
      console.error('Error resolving conflict:', error);
      showSnackbar(error.message || 'Failed to resolve conflict', 'error');
    } finally {
      setResolving(false);
    }
  };

  // Change primary broker for an instrument
  const handleChangePrimaryBroker = async (instrumentId, newBrokerId) => {
    try {
      setResolving(true);
      
      const response = await services.instruments.admin.changePrimaryBroker(
        instrumentId,
        { new_primary_broker_id: newBrokerId, trigger_resync: false }
      );
      
      showSnackbar('Primary broker changed successfully!', 'success');
      setShowResolveModal(false);
      setSelectedConflict(null);
      setConflictDetail(null);
      
      // Refresh data
      fetchStats();
      fetchConflicts();
    } catch (error) {
      console.error('Error changing primary broker:', error);
      showSnackbar(error.message || 'Failed to change primary broker', 'error');
    } finally {
      setResolving(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchConflicts();
  };

  const viewConflictDetail = (conflict) => {
    setSelectedConflict(conflict);
    fetchConflictDetail(conflict.instrument_id);
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const getStatusVariant = () => {
      switch (status) {
        case 'single_source':
          return 'profit';
        case 'auto_resolved':
          return 'info';
        case 'conflict_needs_review':
          return 'loss';
        case 'manual_override':
          return 'role';
        default:
          return 'neutral';
      }
    };

    const getStatusText = () => {
      switch (status) {
        case 'single_source':
          return 'Single Source';
        case 'auto_resolved':
          return 'Auto-Resolved';
        case 'conflict_needs_review':
          return 'Needs Review';
        case 'manual_override':
          return 'Manual Override';
        default:
          return status;
      }
    };

    return (
      <span className={`ds-badge ${getStatusVariant()} sm`}>
        {getStatusText()}
      </span>
    );
  };

  if (user && user.role !== 'admin') {
    const content = (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
        <FiAlertCircle className="text-red-600 dark:text-red-400 text-xl flex-shrink-0" />
        <div>
          <p className="text-red-800 dark:text-red-200 font-semibold">Admin Access Required</p>
          <p className="text-red-600 dark:text-red-400 text-sm">
            You do not have permission to access this page.
          </p>
        </div>
      </div>
    );
    
    return isEmbedded ? content : (
      <div className="m-2 md:m-6 p-4 md:p-8 bg-bg-2 rounded-3xl">
        {content}
      </div>
    );
  }

  const mainContent = (
    <>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="ds-h1 tracking-tight text-slate-900 dark:text-gray-200">
            Reconciliation Dashboard
          </h1>
          <p className="text-fg-2 mt-2">
            Monitor and resolve instrument data conflicts
          </p>
        </div>
        
        <div className="flex gap-3 items-center">
          <label className="flex items-center gap-2 text-sm text-fg-2">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh (30s)
          </label>
          
          <button
            type="button"
            onClick={() => {
              fetchStats();
              fetchConflicts();
              fetchValidationFailures();
            }}
            className="btn btn-primary flex items-center gap-2"
          >
            <FiRefreshCw />
            Refresh
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Total Instruments */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Instruments</p>
              <p className="text-3xl font-bold mt-2">
                {stats?.total_instruments?.toLocaleString() || 0}
              </p>
            </div>
            <FiFileText className="text-5xl text-blue-200 opacity-50" />
          </div>
        </div>

        {/* Single Source */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Single Source</p>
              <p className="text-3xl font-bold mt-2">
                {stats?.single_source?.toLocaleString() || 0}
              </p>
              <p className="text-green-100 text-xs mt-1">
                {stats?.total_instruments > 0 
                  ? `${((stats?.single_source / stats?.total_instruments) * 100).toFixed(1)}%`
                  : '0%'
                }
              </p>
            </div>
            <FiCheckCircle className="text-5xl text-green-200 opacity-50" />
          </div>
        </div>

        {/* Auto-Resolved */}
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-100 text-sm font-medium">Auto-Resolved</p>
              <p className="text-3xl font-bold mt-2">
                {stats?.auto_resolved?.toLocaleString() || 0}
              </p>
              <p className="text-indigo-100 text-xs mt-1">
                {stats?.total_instruments > 0 
                  ? `${((stats?.auto_resolved / stats?.total_instruments) * 100).toFixed(1)}%`
                  : '0%'
                }
              </p>
            </div>
            <FiCheckCircle className="text-5xl text-indigo-200 opacity-50" />
          </div>
        </div>

        {/* Conflicts */}
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm font-medium">Needs Review</p>
              <p className="text-3xl font-bold mt-2">
                {stats?.conflict_needs_review?.toLocaleString() || 0}
              </p>
              <p className="text-red-100 text-xs mt-1">
                {stats?.total_instruments > 0 
                  ? `${((stats?.conflict_needs_review / stats?.total_instruments) * 100).toFixed(1)}%`
                  : '0%'
                }
              </p>
            </div>
            <FiXCircle className="text-5xl text-red-200 opacity-50" />
          </div>
        </div>
      </div>

      {/* Validation Failures Alert */}
      {validationFailures.length > 0 && (
        <div className="mb-6 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <div className="flex items-center gap-3 mb-2">
            <FiAlertTriangle className="text-orange-600 dark:text-orange-400 text-xl" />
            <h3 className="text-orange-900 dark:text-orange-100 font-semibold">
              {validationFailures.length} Validation Failure{validationFailures.length > 1 ? 's' : ''}
            </h3>
          </div>
          <p className="text-orange-700 dark:text-orange-300 text-sm">
            Some instruments have critical validation issues. Review and fix them to ensure data quality.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 bg-bg-3 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="ds-input w-full"
            >
              <option value="">All</option>
              <option value="single_source">Single Source</option>
              <option value="auto_resolved">Auto-Resolved</option>
              <option value="conflict_needs_review">Needs Review</option>
              <option value="manual_override">Manual Override</option>
            </select>
          </div>

          {/* Exchange Filter */}
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-2">
              Exchange
            </label>
            <select
              value={exchangeFilter}
              onChange={(e) => {
                setExchangeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="ds-input w-full"
            >
              <option value="">All Exchanges</option>
              <option value="NSE">NSE</option>
              <option value="BSE">BSE</option>
              <option value="MCX">MCX</option>
              <option value="NFO">NFO</option>
              <option value="BFO">BFO</option>
            </select>
          </div>

          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-fg-2 mb-2">
              Search Symbol
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by symbol..."
                className="ds-input flex-1"
              />
              <button
                type="button"
                onClick={handleSearch}
                className="btn btn-primary flex items-center gap-2"
              >
                <FiSearch />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Conflicts Table */}
      <div className="border border-[var(--divider)] rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
             <Loader text="Loading conflicts..." />
          </div>
        ) : conflicts.length === 0 ? (
          <div className="text-center py-16 text-fg-2">
            <FiCheckCircle className="mx-auto text-6xl mb-4 text-green-500" />
            <p className="text-xl font-semibold">No conflicts found</p>
            <p className="text-sm mt-2">All instruments are in a consistent state</p>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-bg-3">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                    Exchange
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                    Symbol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                    Conflict Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                    Sources
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-bg-2 divide-y divide-gray-200 dark:divide-gray-700">
                {conflicts.map((conflict) => (
                  <tr 
                    key={conflict.instrument_id}
                    className="hover:bg-bg-3 transition-colors cursor-pointer"
                    onClick={() => viewConflictDetail(conflict)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-fg-1">
                      {conflict.exchange}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2">
                      {conflict.symbol}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2">
                      {conflict.instrument_type}
                    </td>
                    <td className="px-6 py-4 text-sm text-fg-2 max-w-xs">
                      <div className="flex flex-col gap-1">
                        <span className="text-fg-1">
                          {conflict.conflict_reason || 'Data mismatch detected'}
                        </span>
                        {conflict.conflicting_fields?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {conflict.conflicting_fields.slice(0, 3).map((field) => (
                              <span key={field} className="ds-badge loss xs">
                                {field}
                              </span>
                            ))}
                            {conflict.conflicting_fields.length > 3 && (
                              <span className="text-xs text-fg-2">
                                +{conflict.conflicting_fields.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="ds-badge neutral xs">
                        {conflict.conflicting_brokers?.length || conflict.broker_count || 0} broker{(conflict.conflicting_brokers?.length || conflict.broker_count || 0) !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={conflict.reconciliation_status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            viewConflictDetail(conflict);
                          }}
                          className="btn btn-ghost btn-sm flex items-center gap-1"
                          title="View Details"
                        >
                          <FiEye className="text-sm" />
                          View
                        </button>
                        {conflict.reconciliation_status === 'conflict_needs_review' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              viewConflictDetail(conflict);
                            }}
                            className="text-green-600 dark:text-green-400 hover:underline flex items-center gap-1"
                            title="Resolve Conflict"
                          >
                            <FiCheck className="text-sm" />
                            Resolve
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-bg-3 px-6 py-4 flex items-center justify-between">
                <div className="text-sm text-fg-2">
                  Showing {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, totalRecords)} of {totalRecords} | Page {currentPage} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white dark:bg-bg-3 border border-[var(--divider)] 
                             rounded-lg hover:bg-bg-3 dark:hover:bg-bg-3 transition-colors
                             disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-white dark:bg-bg-3 border border-[var(--divider)] 
                             rounded-lg hover:bg-bg-3 dark:hover:bg-bg-3 transition-colors
                             disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Conflict Resolution Modal */}
      {showResolveModal && conflictDetail && (
        <ConflictResolveModal
          conflict={conflictDetail}
          onClose={() => {
            setShowResolveModal(false);
            setSelectedConflict(null);
            setConflictDetail(null);
          }}
          onResolve={handleResolveConflict}
          onChangePrimary={handleChangePrimaryBroker}
          resolving={resolving}
        />
      )}
    </>
  );

  return isEmbedded ? mainContent : (
    <div className="m-2 md:m-6 p-4 md:p-8 bg-bg-2 rounded-3xl">
      {mainContent}
    </div>
  );
};

/**
 * Conflict Resolution Modal Component
 * 
 * Shows detailed conflict view with side-by-side broker data comparison
 * Allows admin to select winning broker data or apply manual overrides
 */
const ConflictResolveModal = ({ conflict, onClose, onResolve, onChangePrimary, resolving }) => {
  const [selectedBrokerId, setSelectedBrokerId] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideData, setOverrideData] = useState({});

  // Debug: Log broker_data to verify broker_id uniqueness
  console.log('ConflictResolveModal broker_data:', conflict.broker_data?.map(b => ({ id: b.broker_id, name: b.broker_name })));
  console.log('Selected broker ID:', selectedBrokerId);

  // Helper to compare broker_id consistently (handles string/number mismatch)
  const isBrokerSelected = (brokerId) => {
    if (selectedBrokerId === null) return false;
    return Number(selectedBrokerId) === Number(brokerId);
  };

  // Helper to select broker (store as number)
  const selectBroker = (brokerId) => {
    console.log('Selecting broker:', brokerId);
    setSelectedBrokerId(Number(brokerId));
  };

  // Field labels for display
  const fieldLabels = {
    symbol: 'Symbol',
    name: 'Name',
    lot_size: 'Lot Size',
    tick_size: 'Tick Size',
    isin: 'ISIN'
  };

  const handleApprove = () => {
    if (!selectedBrokerId) return;
    
    onResolve(
      selectedBrokerId,
      overrideMode && Object.keys(overrideData).length > 0 ? overrideData : null,
      resolutionNotes
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-2 rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-[var(--divider)] flex justify-between items-center">
          <div>
            <h2 className="ds-h2 text-fg-1">
              Resolve Conflict: {conflict.symbol}
            </h2>
            <p className="text-sm text-fg-2">
              {conflict.exchange} • {conflict.instrument_type}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-bg-3 rounded-lg transition-colors"
          >
            <FiX className="text-xl" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
          {/* Current Master Data */}
          <div className="mb-6">
            <h3 className="font-semibold text-fg-1 mb-3 flex items-center gap-2">
              <FiFileText />
              Current Master Data
            </h3>
            <div className="bg-bg-1 rounded-lg p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(conflict.current_data || {}).map(([key, value]) => (
                <div key={key}>
                  <span className="text-xs text-fg-2 block">
                    {fieldLabels[key] || key}
                  </span>
                  <span className="text-sm font-medium text-fg-1">
                    {value ?? 'N/A'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Conflicting Fields */}
          {conflict.conflicting_fields?.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2 flex items-center gap-2">
                <FiAlertTriangle />
                Conflicting Fields
              </h4>
              <div className="flex flex-wrap gap-2">
                {conflict.conflicting_fields.map(field => (
                  <span key={field} className="ds-badge loss sm">
                    {fieldLabels[field] || field}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Broker Data Comparison */}
          <div className="mb-6">
            <h3 className="font-semibold text-fg-1 mb-3">
              Select Source Broker Data
            </h3>
            <p className="text-sm text-fg-2 mb-4">
              Choose which broker's data to use for resolving this conflict. The selected broker's data will update the master record.
            </p>
            
            <div className="grid gap-4">
              {(conflict.broker_data || []).map((broker, index) => (
                <div
                  key={`broker-${broker.broker_id}-${index}`}
                  onClick={() => selectBroker(broker.broker_id)}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    isBrokerSelected(broker.broker_id)
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500'
                      : 'border-[var(--divider)] hover:border-[var(--divider)] dark:hover:border-[var(--divider)]'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isBrokerSelected(broker.broker_id)
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-[var(--divider)]'
                      }`}>
                        {isBrokerSelected(broker.broker_id) && (
                          <FiCheck className="text-white text-xs" />
                        )}
                      </div>
                      <div>
                        <span className="font-semibold text-fg-1">
                          {broker.broker_name}
                        </span>
                        {Number(conflict.primary_broker_id) === Number(broker.broker_id) && (
                          <span className="ds-badge profit xs ml-2">
                            Primary
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-fg-2">
                      Last synced: {broker.last_synced ? new Date(broker.last_synced).toLocaleString() : 'Unknown'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <span className="text-xs text-fg-2 block">Symbol</span>
                      <span className={`text-sm font-medium ${
                        conflict.conflicting_fields?.includes('symbol') 
                          ? 'text-red-600 dark:text-red-400' 
                          : 'text-fg-1'
                      }`}>
                        {broker.symbol || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-fg-2 block">Name</span>
                      <span className={`text-sm font-medium ${
                        conflict.conflicting_fields?.includes('name') 
                          ? 'text-red-600 dark:text-red-400' 
                          : 'text-fg-1'
                      }`}>
                        {broker.name || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-fg-2 block">Lot Size</span>
                      <span className={`text-sm font-medium ${
                        conflict.conflicting_fields?.includes('lot_size') 
                          ? 'text-red-600 dark:text-red-400' 
                          : 'text-fg-1'
                      }`}>
                        {broker.lot_size ?? 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-fg-2 block">Tick Size</span>
                      <span className={`text-sm font-medium ${
                        conflict.conflicting_fields?.includes('tick_size') 
                          ? 'text-red-600 dark:text-red-400' 
                          : 'text-fg-1'
                      }`}>
                        {broker.tick_size ?? 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-fg-2 block">ISIN</span>
                      <span className={`text-sm font-medium ${
                        conflict.conflicting_fields?.includes('isin') 
                          ? 'text-red-600 dark:text-red-400' 
                          : 'text-fg-1'
                      }`}>
                        {broker.isin || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Validation Issues */}
          {conflict.validation_issues?.length > 0 && (
            <div className="mb-6 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
              <h4 className="font-semibold text-orange-800 dark:text-orange-200 mb-2">
                Validation Issues ({conflict.validation_issues.length})
              </h4>
              <ul className="space-y-2">
                {conflict.validation_issues.map((issue, idx) => (
                  <li key={idx} className="text-sm text-orange-700 dark:text-orange-300 flex items-start gap-2">
                    <span className={`ds-badge xs ${
                      issue.severity === 'critical' ? 'loss' : 'warn'
                    }`}>
                      {issue.severity}
                    </span>
                    <span>{issue.field}: {issue.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Resolution Notes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-fg-2 mb-2">
              Resolution Notes (Optional)
            </label>
            <textarea
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="Add notes about why this resolution was chosen..."
              className="ds-input w-full"
              rows={3}
            />
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-[var(--divider)] flex justify-between items-center">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          
          <div className="flex gap-3">
            {selectedBrokerId && Number(selectedBrokerId) !== Number(conflict.primary_broker_id) && (
              <button
                type="button"
                onClick={() => onChangePrimary(conflict.instrument_id, selectedBrokerId)}
                disabled={resolving || !selectedBrokerId}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {resolving ? <FiRefreshCw className="animate-spin" /> : <FiEdit />}
                Set as Primary
              </button>
            )}
            
            <button
              type="button"
              onClick={handleApprove}
              disabled={resolving || !selectedBrokerId}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {resolving ? <FiRefreshCw className="animate-spin" /> : <FiCheck />}
              Approve & Update Master
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReconciliationDashboard;
